import { AskableResources, LANGUAGES, LOCALES } from '@askableorg/i18n';
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

import enClients from './namespaces/clients.json';
import enComponents from './namespaces/components.json';
import enDocbuilder from './namespaces/docbuilder.json';
import enSessions from './namespaces/sessions.json';
import { LOCALE_LOADERS, isAppNamespace } from './resources';

import type { BackendModule, ResourceKey } from 'i18next';

export const DEFAULT_RESOURCES = {
  en: {
    sessions: enSessions,
    clients: enClients,
    components: enComponents,
    docbuilder: enDocbuilder,
  },
};

export type EN_RESOURCES = (typeof DEFAULT_RESOURCES)['en'];

export { bundledResources } from './resources';
export type { AppNamespace } from './resources';

/** Resolves to `null` for a language the namespace has no file for. */
type NamespaceLoader = (language: string) => Promise<ResourceKey | null>;

export type LocaleBackendOptions = {
  /** Namespaces owned outside this package, each loaded per locale. */
  readonly namespaceLoaders?: Readonly<Record<string, NamespaceLoader>>;
};

let namespaceLoaders: NonNullable<LocaleBackendOptions['namespaceLoaders']> = {};

// i18next accepts one backend. `AskableResources` keeps its options and the `common` and `zod`
// namespaces; this backend adds the app namespaces, one lazy chunk for each locale.
const LocaleBackend: BackendModule<LocaleBackendOptions> = {
  type: 'backend',
  // i18next hands over its `backend` option as is, so a host that sets none passes `undefined`.
  init: (services, backendOptions: LocaleBackendOptions | undefined, i18nextOptions) => {
    namespaceLoaders = backendOptions?.namespaceLoaders ?? {};
    AskableResources.init(services, backendOptions ?? {}, i18nextOptions);
  },
  read: (language, namespace, callback) => {
    const loadOwnedNamespace = Object.hasOwn(namespaceLoaders, namespace) ? namespaceLoaders[namespace] : undefined;

    if (loadOwnedNamespace) {
      loadOwnedNamespace(language).then(
        resources => callback(null, resources),
        (error: Error) => callback(error, null),
      );
      return;
    }

    if (!isAppNamespace(namespace)) {
      AskableResources.read(language, namespace, callback);
      return;
    }

    const load = LOCALE_LOADERS[language as keyof typeof LOCALE_LOADERS]?.[namespace];

    // A region code such as `de-DE` has no file; i18next then loads `de`.
    if (!load) {
      callback(null, null);
      return;
    }

    load().then(
      module => callback(null, module.default),
      (error: Error) => callback(error, null),
    );
  },
};

const i18n = i18next.use(initReactI18next).use(LocaleBackend);

export { i18n, LANGUAGES, LOCALES };
